import React from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import { EditableText } from '../components/EditableText';
import { InfoNotice } from '../../components/InfoNotice';

export interface Testimonial {
  name: string;
  role: string;
  text: string;
  rating: number;
}

export interface TestimonialsContent {
  headerTagline: string;
  sectionTitle: string;
  testimonials: Testimonial[];
  googleReviewsLink: string;
  useGoogleReviews: boolean;
}

interface TestimonialsSectionProps {
  content: TestimonialsContent;
  onUpdate: (updates: Partial<TestimonialsContent>) => void;
  organizationId: string;
  isPreview: boolean;
  accentColor?: string;
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  content,
  onUpdate,
  organizationId,
  isPreview,
  accentColor,
}) => {
  const addTestimonial = () => {
    const newTestimonial: Testimonial = {
      name: '[Client Name]',
      role: '[Client Role/Location]',
      text: '[Share their experience and feedback]',
      rating: 5,
    };
    onUpdate({ testimonials: [...content.testimonials, newTestimonial] });
  };

  const removeTestimonial = (index: number) => {
    const updatedTestimonials = content.testimonials.filter((_, i) => i !== index);
    onUpdate({ testimonials: updatedTestimonials });
  };

  const updateTestimonial = (index: number, field: keyof Testimonial, value: string | number) => {
    const updatedTestimonials = [...content.testimonials];
    updatedTestimonials[index] = { ...updatedTestimonials[index], [field]: value };
    onUpdate({ testimonials: updatedTestimonials });
  };

  const renderStars = (rating: number, testimonialIndex: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => !isPreview && updateTestimonial(testimonialIndex, 'rating', star)}
            className={`${!isPreview ? 'cursor-pointer' : 'cursor-default'}`}
            disabled={isPreview}
          >
            <Star
              className={`w-5 h-5 ${
                star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full bg-white py-16">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Header Tagline - Not Editable */}
          <div className="mb-4">
            <div
              className="font-bold text-sm uppercase tracking-wider"
              style={{ color: accentColor || '#FACC15' }}
            >
              {content.headerTagline}
            </div>
          </div>
          <div className="text-gray-900 text-5xl font-bold">{content.sectionTitle}</div>
        </div>

        {/* Google Reviews Option */}
        {!isPreview && (
          <div className="mb-8">
            <div className="flex items-start gap-3 mb-4">
              <input
                type="checkbox"
                id="useGoogleReviews"
                checked={content.useGoogleReviews}
                onChange={(e) => onUpdate({ useGoogleReviews: e.target.checked })}
                className="mt-1 w-5 h-5 text-blue-600 cursor-pointer"
              />
              <label
                htmlFor="useGoogleReviews"
                className="text-gray-900 font-semibold text-lg cursor-pointer"
              >
                Use Google Reviews
              </label>
            </div>

            <InfoNotice>
              Check the box above if you want to display Google Reviews on your final website. Your
              Google Reviews widget will be professionally integrated in the final output.
            </InfoNotice>

            {content.useGoogleReviews && (
              <div className="mt-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Google Reviews Link / Place ID:
                </label>
                <input
                  type="text"
                  value={content.googleReviewsLink}
                  onChange={(e) => onUpdate({ googleReviewsLink: e.target.value })}
                  placeholder="Enter your Google Reviews link or Place ID"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Note: The Google Reviews widget will be added to your final website design.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Display Google Reviews Notice in Preview */}
        {isPreview && content.useGoogleReviews && (
          <div className="mb-8">
            <InfoNotice>
              <div className="text-center">
                <Star className="w-8 h-8 mx-auto mb-2 text-yellow-400 fill-yellow-400" />
                <p className="font-semibold">Google Reviews Enabled</p>
                <p className="text-sm mt-1">
                  Your Google Reviews will be professionally integrated in the final website design
                </p>
              </div>
            </InfoNotice>
          </div>
        )}

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {content.testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow relative"
            >
              {/* Delete Button */}
              {!isPreview && content.testimonials.length > 1 && (
                <button
                  onClick={() => removeTestimonial(index)}
                  className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                  title="Remove testimonial"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}

              {/* Rating Stars */}
              <div className="mb-4">{renderStars(testimonial.rating, index)}</div>

              {/* Testimonial Text */}
              <div className="mb-4">
                <div className="text-gray-700  leading-relaxed">
                  <EditableText
                    value={testimonial.text}
                    onChange={(value) => updateTestimonial(index, 'text', value)}
                    isPreview={isPreview}
                    multiline
                    className="leading-relaxed"
                  />
                </div>
              </div>

              {/* Client Info */}
              <div className="border-t border-gray-200 pt-4">
                <div className="text-gray-900 font-bold">
                  <EditableText
                    value={testimonial.name}
                    onChange={(value) => updateTestimonial(index, 'name', value)}
                    isPreview={isPreview}
                  />
                </div>
                <div className="text-gray-600 text-sm mt-1">
                  <EditableText
                    value={testimonial.role}
                    onChange={(value) => updateTestimonial(index, 'role', value)}
                    isPreview={isPreview}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Testimonial Button */}
        {!isPreview && (
          <div className="flex justify-center">
            <button
              onClick={addTestimonial}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Testimonial
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
